/**
 * Client-side artwork processing.
 *
 * Every accepted file is decoded, measured and analysed in the browser
 * before any upload: type by magic bytes, pixel size, transparency, white
 * background, SVG sanitising and PDF rasterising. The result carries a
 * drawable image for the editor and facts for the RFQ.
 *
 * Future hooks: `ArtworkAnalyzer` implementations (AI quality check,
 * background removal) receive the same ProcessedArtwork.
 */
import { ARTWORK_MAX_BYTES, ARTWORK_MIME, sniffFormat, type ArtworkFormat } from '@shared/fileTypes';
import { sanitizeSvg } from './sanitizeSvg';

export type ArtworkWarning =
  | { code: 'whiteBackground' }
  | { code: 'noTransparencyJpeg' }
  | { code: 'svgText' }
  | { code: 'svgSanitized'; n: number }
  | { code: 'svgRaster' }
  | { code: 'pdfPages'; n: number };

export interface ProcessedArtwork {
  localId: string;
  file: File;
  fileName: string;
  format: ArtworkFormat;
  mime: string;
  sizeBytes: number;
  sha256: string;
  kind: 'raster' | 'vector';
  /** Source pixel size for rasters; null for vectors. */
  pixelWidth: number | null;
  pixelHeight: number | null;
  /** Width / height of the artwork. */
  aspect: number;
  /** Decoded, drawable image (downscaled for rasters above 4096 px). */
  image: HTMLCanvasElement | ImageBitmap;
  /** Object URL for thumbnails; revoke with disposeArtwork. */
  thumbUrl: string;
  hasTransparency: boolean;
  pdf?: { pages: number; pageWidthMm: number; pageHeightMm: number };
  svg?: { viewBox: string | null; removed: number };
  /** What gets uploaded: the original file, or the sanitised SVG markup. */
  uploadBlob: Blob;
  warnings: ArtworkWarning[];
}

export class ArtworkError extends Error {
  constructor(public readonly code: 'type' | 'size' | 'decode' | 'pdf' | 'svg') {
    super(code);
  }
}

const MAX_DRAW_PX = 4096;
const VECTOR_RASTER_PX = 3000;

export async function processArtwork(file: File): Promise<ProcessedArtwork> {
  if (file.size > ARTWORK_MAX_BYTES) throw new ArtworkError('size');
  if (file.size === 0) throw new ArtworkError('decode');

  const buffer = await file.arrayBuffer();
  const format = sniffFormat(new Uint8Array(buffer.slice(0, 1024)));
  if (!format) throw new ArtworkError('type');

  const sha256 = await digest(buffer);
  const base = {
    localId: crypto.randomUUID?.() ?? String(Date.now()),
    file,
    fileName: file.name,
    format,
    mime: ARTWORK_MIME[format],
    sizeBytes: file.size,
    sha256,
  };

  const processed =
    format === 'png' || format === 'jpeg' ? await processRaster(buffer, format) : format === 'svg' ? await processSvg(buffer) : await processPdf(buffer);
  return { ...base, ...processed, uploadBlob: processed.uploadBlob ?? file };
}

type Processed = Omit<ProcessedArtwork, 'localId' | 'file' | 'fileName' | 'format' | 'mime' | 'sizeBytes' | 'sha256' | 'uploadBlob'> & { uploadBlob?: Blob };

async function processRaster(buffer: ArrayBuffer, format: 'png' | 'jpeg'): Promise<Processed> {
  const blob = new Blob([buffer], { type: ARTWORK_MIME[format] });
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    throw new ArtworkError('decode');
  }
  const w = bitmap.width;
  const h = bitmap.height;
  if (!w || !h) throw new ArtworkError('decode');

  let image: HTMLCanvasElement | ImageBitmap = bitmap;
  if (Math.max(w, h) > MAX_DRAW_PX) {
    const k = MAX_DRAW_PX / Math.max(w, h);
    image = drawToCanvas(bitmap, Math.round(w * k), Math.round(h * k));
    bitmap.close();
  }

  const stats = analysePixels(image);
  const warnings: ArtworkWarning[] = [];
  if (format === 'jpeg') warnings.push({ code: 'noTransparencyJpeg' });
  if (!stats.hasTransparency && stats.whiteBorder) warnings.push({ code: 'whiteBackground' });

  return {
    kind: 'raster',
    pixelWidth: w,
    pixelHeight: h,
    aspect: w / h,
    image,
    thumbUrl: URL.createObjectURL(blob),
    hasTransparency: stats.hasTransparency,
    warnings,
  };
}

async function processSvg(buffer: ArrayBuffer): Promise<Processed> {
  const text = new TextDecoder().decode(buffer);
  const clean = sanitizeSvg(text);
  if (!clean) throw new ArtworkError('svg');

  const blob = new Blob([clean.markup], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  try {
    await img.decode();
  } catch {
    URL.revokeObjectURL(url);
    throw new ArtworkError('svg');
  }
  const aspect = clean.width / clean.height;
  const k = VECTOR_RASTER_PX / Math.max(clean.width, clean.height);
  const canvas = drawToCanvas(img, Math.max(1, Math.round(clean.width * k)), Math.max(1, Math.round(clean.height * k)));
  const stats = analysePixels(canvas);

  const warnings: ArtworkWarning[] = [];
  if (clean.removed > 0) warnings.push({ code: 'svgSanitized', n: clean.removed });
  if (clean.hasLiveText) warnings.push({ code: 'svgText' });
  if (clean.hasRaster) warnings.push({ code: 'svgRaster' });
  if (!stats.hasTransparency && stats.whiteBorder) warnings.push({ code: 'whiteBackground' });

  return {
    kind: 'vector',
    pixelWidth: null,
    pixelHeight: null,
    aspect,
    image: canvas,
    thumbUrl: url,
    hasTransparency: stats.hasTransparency,
    svg: { viewBox: clean.viewBox, removed: clean.removed },
    uploadBlob: blob,
    warnings,
  };
}

async function processPdf(buffer: ArrayBuffer): Promise<Processed> {
  let result;
  try {
    const { renderPdfFirstPage } = await import('./pdf');
    result = await renderPdfFirstPage(buffer.slice(0), VECTOR_RASTER_PX);
  } catch (err) {
    console.warn('[artwork] pdf failed', err);
    throw new ArtworkError('pdf');
  }
  const { canvas, pages, pageWidthPt, pageHeightPt } = result;
  const stats = analysePixels(canvas);
  const warnings: ArtworkWarning[] = [];
  if (pages > 1) warnings.push({ code: 'pdfPages', n: pages });
  if (!stats.hasTransparency && stats.whiteBorder) warnings.push({ code: 'whiteBackground' });

  const thumb = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/png'));
  return {
    kind: 'vector',
    pixelWidth: null,
    pixelHeight: null,
    aspect: pageWidthPt / pageHeightPt,
    image: canvas,
    thumbUrl: thumb ? URL.createObjectURL(thumb) : '',
    hasTransparency: stats.hasTransparency,
    pdf: { pages, pageWidthMm: (pageWidthPt * 25.4) / 72, pageHeightMm: (pageHeightPt * 25.4) / 72 },
    warnings,
  };
}

export function disposeArtwork(a: ProcessedArtwork | null) {
  if (!a) return;
  if (a.thumbUrl) URL.revokeObjectURL(a.thumbUrl);
  if ('close' in a.image) a.image.close();
}

/** Effective print resolution at a physical width. Null for vectors. */
export function effectiveDpi(a: ProcessedArtwork, widthMm: number): number | null {
  if (a.kind === 'vector' || !a.pixelWidth || widthMm <= 0) return null;
  return Math.round(a.pixelWidth / (widthMm / 25.4));
}

export type DpiLevel = 'good' | 'ok' | 'low' | 'vector';
export function dpiLevel(dpi: number | null): DpiLevel {
  if (dpi == null) return 'vector';
  if (dpi >= 300) return 'good';
  if (dpi >= 150) return 'ok';
  return 'low';
}

// ---------------------------------------------------------------- helpers

function drawToCanvas(src: CanvasImageSource, w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, w, h);
  return c;
}

/** Transparency and white-border detection on a 256 px sample. */
function analysePixels(src: HTMLCanvasElement | ImageBitmap): { hasTransparency: boolean; whiteBorder: boolean } {
  const k = 256 / Math.max(src.width, src.height);
  const w = Math.max(1, Math.round(src.width * Math.min(1, k)));
  const h = Math.max(1, Math.round(src.height * Math.min(1, k)));
  const c = drawToCanvas(src, w, h);
  const data = c.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, w, h).data;

  let transparent = 0;
  for (let i = 3; i < data.length; i += 4) if (data[i] < 250) transparent++;
  const hasTransparency = transparent / (w * h) > 0.001;

  let border = 0;
  let white = 0;
  const isWhite = (x: number, y: number) => {
    const i = (y * w + x) * 4;
    return data[i + 3] > 240 && data[i] > 238 && data[i + 1] > 238 && data[i + 2] > 238;
  };
  for (let x = 0; x < w; x++) {
    for (const y of [0, h - 1]) {
      border++;
      if (isWhite(x, y)) white++;
    }
  }
  for (let y = 1; y < h - 1; y++) {
    for (const x of [0, w - 1]) {
      border++;
      if (isWhite(x, y)) white++;
    }
  }
  return { hasTransparency, whiteBorder: border > 0 && white / border > 0.85 };
}

async function digest(buffer: ArrayBuffer): Promise<string> {
  if (!crypto?.subtle) return '';
  const d = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
